import { PlusIcon } from '@heroicons/react/24/outline'
import React from 'react'

import Action from '@/gf/components/Action'
import Card from '@/gf/components/Card'
import TextField from '@/gf/components/TextField'
import useDebounce from '@/gf/hooks/useDebounce'
import useSearchCustomers from '@/gf/hooks/useSearchCustomers'
import useToggle from '@/gf/hooks/useToggle'
import UserContext from '@/gf/modules/User'
import { CreateOrderFormCustomer, Customer } from '@/types'
import useSession from '../hooks/useSession'
import AddCustomerModal from './AddCustomerModal/AddCustomerModal'

type Props = {
  setOrderCustomer: (customer?: CreateOrderFormCustomer) => void
}

const AddCustomerCard = ({ setOrderCustomer }: Props) => {
  const { user, store } = useSession()

  const [customersFromSearch, setCustomersFromSearch] = React.useState<Customer[]>([])
  const [query, setQuery] = React.useState('')

  const [searchLoading, searchLoadingToggler] = useToggle(false)
  const [showSearch, showSearchToggler] = useToggle(false)
  const [addCustomerModalOpen, addCustomerModalToggler] = useToggle(false)

  const { refetch } = useSearchCustomers({ skip: true })

  const debouncedQuery = useDebounce(query, 500)

  const queryRef = React.useRef<string>()
  queryRef.current = debouncedQuery

  const searchCustomers = () => {
    if (debouncedQuery === '') {
      setCustomersFromSearch([])
      showSearchToggler.off()
      return
    }

    showSearchToggler.on()
    searchLoadingToggler.on()

    const filter = JSON.stringify(
      UserContext.isAdmin(user)
        ? [
            'or',
            ['contains', 'name', debouncedQuery],
            ['contains', 'email', debouncedQuery],
            ['organization_name_contains', debouncedQuery],
          ]
        : [
            [
              'or',
              ['contains', 'name', debouncedQuery],
              ['organization_name_contains', debouncedQuery],
            ],
            ['equals', 'store_id', store.id],
          ]
    )

    refetch({ filter }).then(({ data: { customersSearch } }) => {
      if (debouncedQuery === queryRef.current) {
        setCustomersFromSearch(customersSearch.customers)
        searchLoadingToggler.off()
      }
    })
  }

  React.useEffect(() => {
    searchCustomers()
  }, [debouncedQuery])

  return (
    <>
      <AddCustomerModal
        open={addCustomerModalOpen}
        onClose={addCustomerModalToggler.off}
        onSubmit={(newCustomer) => {
          setOrderCustomer(newCustomer)
          addCustomerModalToggler.off()
        }}
      />

      <Card title="Find or create a customer">
        <Card.Section>
          <TextField
            label="Search customer"
            placeholder={`Search by name${UserContext.isAdmin(user) ? ' or email' : ''}`}
            onChange={(e) => setQuery(e.target.value)}
          />

          <Action.S onClick={addCustomerModalToggler.on} className="mt-4">
            <PlusIcon className="inline-block h-5 w-5 text-gray-700 -mt-1" /> Add new customer
          </Action.S>
        </Card.Section>
        {showSearch && (
          <Card.Section>
            {searchLoading ? (
              <div className="animate-spin-slow border-t-gearflow ease-linear rounded-full border-8 border-t-8 border-gray-200 h-16 w-16 mx-auto" />
            ) : (
              <div className="max-h-80 overflow-y-scroll">
                <Card.SubSection>
                  {customersFromSearch.map((customer) => (
                    <div
                      key={customer.id}
                      className="py-3 px-4 rounded-md hover:bg-blue-100 cursor-pointer text-sm"
                      onClick={() => {
                        setOrderCustomer(customer)
                        showSearchToggler.off()
                        setCustomersFromSearch([])
                      }}
                    >
                      <p className="font-bold">{customer.account.name}</p>
                      <p className="font-bold">{customer.name}</p>
                      <p className="text-gray-500">
                        {UserContext.isAdmin(user)
                          ? customer.email
                          : customer.phoneNumber || 'No phone number'}
                      </p>
                    </div>
                  ))}
                </Card.SubSection>
              </div>
            )}
          </Card.Section>
        )}
      </Card>
    </>
  )
}

export default AddCustomerCard
